import axios, { type AxiosRequestConfig } from 'axios'

import { connectorAPI } from '../actions/connector'
import { appSelectors } from '@appStore/reducers/app'
import store from '@appStore/store'
import { notificationUtils } from 'features/notifications'
import {
	BackendValidationError,
	BadGatewayError,
	NotAuthorizedError,
	RequestTimedOutError
} from '../shared/errors'
import { createProxyUnavailableError } from '../shared/errors/ProxyUnavailableError'

export const httpRequest = async <R = any>(data: AxiosRequestConfig) => {
	let startTime = Date.now()
	try {
		const token = await connectorAPI.getAccessToken()
		axios.defaults.headers.common['Authorization'] = token

		//defined once again in a case if getAccessToken was slow, exchanged tokens
		startTime = Date.now()

		const result = await axios<R>(data)
		return result
	} catch (error: any) {
		const endTime = Date.now()
		if (
			error.response?.status === 502 ||
			error.response?.status === 503 ||
			error.response?.status === 504 ||
			error.message.includes('Network Error')
		) {
			throw new BadGatewayError(
				'Bad network or service is temporarily unavailable.'
			)
		}
		if (error.response?.status === 551) {
			throw createProxyUnavailableError()
		}
		if (error.response?.status === 422) {
			throw new BackendValidationError(error.response.data.errors)
		}
		if (error.response?.status === 403) {
			const state = store.getState()
			const isOpsSession = appSelectors.appProfile(state)?.isOpsSession

			if (isOpsSession) {
				// show info notification and swallow error for ops read-only sessions
				notificationUtils.show(
					'You are in ops session without write access'
				)
				return
			}

			throw new NotAuthorizedError('Not authorized')
		}

		if (error.message.includes('timeout exceeded')) {
			throw new RequestTimedOutError(
				`${data.method} ${data.url} is timed out, took ${endTime - startTime}`
			)
		}

		if (error) {
			throw new HttpRequestError(error)
		}
	}
}

export class HttpRequestError extends Error {
	get axios() {
		return this.#axiosError
	}

	get skipLogging() {
		return true
	}

	#axiosError

	constructor(axiosError: any) {
		super(axiosError.message)
		this.#axiosError = axiosError
		this.name = 'HttpRequestError'

		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, HttpRequestError)
		}
	}
}
